/**
 * Approval Modal Component
 * Dialog for approving or rejecting evidence with comments
 */
import React, { useState } from 'react';
import { 
  CheckCircle2, 
  XCircle, 
  MessageSquare,
  AlertTriangle,
  User,
  X
} from 'lucide-react';

export default function ApprovalModal({ 
  isOpen, 
  onClose, 
  item, 
  mode = 'approve', // 'approve' or 'reject'
  onSubmit,
  isLoading = false
}) {
  const [comments, setComments] = useState('');
  const [reason, setReason] = useState('');
  const [requestedChanges, setRequestedChanges] = useState('');
  
  if (!isOpen || !item) return null;
  
  const isApprove = mode === 'approve';
  
  const handleSubmit = () => {
    if (isApprove) {
      onSubmit({ comments });
    } else {
      if (!reason.trim()) {
        alert('Please provide a reason for rejection');
        return;
      }
      onSubmit({ reason, requestedChanges });
    }
  };
  
  const handleClose = () => {
    setComments('');
    setReason('');
    setRequestedChanges('');
    onClose();
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#111111] border border-[#2F2F2F] rounded-xl w-full max-w-lg mx-4 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className={`px-6 py-4 border-b border-[#2F2F2F] ${isApprove ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isApprove ? (
                <CheckCircle2 size={24} className="text-emerald-400" />
              ) : (
                <XCircle size={24} className="text-red-400" />
              )}
              <h2 className="text-lg font-semibold text-white">
                {isApprove ? 'Approve Evidence' : 'Reject Evidence'}
              </h2>
            </div>
            <button 
              onClick={handleClose}
              className="p-1 hover:bg-[#2F2F2F] rounded-lg transition-colors"
            >
              <X size={20} className="text-[#6B6B6B]" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Evidence Info */}
          <div className="p-3 bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg">
            <p className="text-sm font-medium text-white truncate">{item.fileName}</p>
            <div className="flex items-center gap-2 mt-1 text-xs text-[#6B6B6B]">
              <User size={10} />
              <span>Owner: {item.owner?.name || 'Unknown'}</span>
            </div>
          </div>
          
          {isApprove ? (
            /* Approve Mode */
            <div>
              <label className="block text-sm text-[#A0A0A0] mb-2">
                <MessageSquare size={12} className="inline mr-1" />
                Comments (optional)
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any comments about this approval..."
                className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg text-sm text-white placeholder:text-[#4F4F4F] focus:border-emerald-500/50 focus:outline-none resize-none"
                rows={3}
                data-testid="approval-comments-input"
              />
            </div>
          ) : (
            /* Reject Mode */
            <>
              <div>
                <label className="block text-sm text-[#A0A0A0] mb-2">
                  <AlertTriangle size={12} className="inline mr-1 text-red-400" />
                  Reason for Rejection <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Explain why this evidence is being rejected..."
                  className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg text-sm text-white placeholder:text-[#4F4F4F] focus:border-red-500/50 focus:outline-none resize-none"
                  rows={3}
                  required
                  data-testid="rejection-reason-input"
                />
              </div>
              <div>
                <label className="block text-sm text-[#A0A0A0] mb-2">
                  <MessageSquare size={12} className="inline mr-1" />
                  Requested Changes (optional)
                </label>
                <textarea
                  value={requestedChanges}
                  onChange={(e) => setRequestedChanges(e.target.value)}
                  placeholder="What changes should the owner make?"
                  className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg text-sm text-white placeholder:text-[#4F4F4F] focus:border-amber-500/50 focus:outline-none resize-none"
                  rows={2}
                  data-testid="requested-changes-input"
                />
              </div>
            </>
          )}
          
          {/* Impact Notice */}
          <div className={`p-3 rounded-lg border ${isApprove ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
            <p className="text-xs text-[#A0A0A0]">
              {isApprove ? (
                <>
                  <CheckCircle2 size={10} className="inline mr-1 text-emerald-400" />
                  This will {item.currentApprovalLevel >= 3 ? 'fully approve' : `advance to Level ${(item.currentApprovalLevel || 1) + 1} approval for`} this evidence.
                </>
              ) : (
                <>
                  <AlertTriangle size={10} className="inline mr-1 text-red-400" />
                  This will notify <span className="text-white">{item.owner?.name || 'the owner'}</span> and return the evidence for revision.
                </>
              )}
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#2F2F2F] bg-[#0A0A0A] flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-[#A0A0A0] hover:text-white hover:bg-[#1F1F1F] rounded-lg transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || (!isApprove && !reason.trim())}
            className={`
              px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2
              ${isApprove 
                ? 'bg-emerald-500 hover:bg-emerald-600 text-white disabled:bg-emerald-500/50' 
                : 'bg-red-500 hover:bg-red-600 text-white disabled:bg-red-500/50'
              }
            `}
            data-testid={isApprove ? 'confirm-approve-btn' : 'confirm-reject-btn'}
          >
            {isLoading ? (
              <span className="animate-pulse">Processing...</span>
            ) : (
              <>
                {isApprove ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                {isApprove ? 'Confirm Approval' : 'Confirm Rejection'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
